import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api/Api";
import { ArrowLeft, Plus, Trash2, CheckCircle } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css"; 

const FeedbackCategoryAddPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([{ item_name: "" }]);
  const [loading, setLoading] = useState(false);

  const handleChange = (index, value) => {
    const updated = [...rows];
    updated[index].item_name = value;
    setRows(updated);
  };

  const addMore = () => {
    if (rows.length >= 10) {
      toast.error("Maximum 10 categories allowed");
      return;
    }
    setRows([...rows, { item_name: "" }]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) {
      toast.error("At least one row is required");
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    for (let row of rows) {
      if (!row.item_name.trim()) {
        toast.error("Category name is required for all rows");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await api.post("/adminmod/feedback-category-add", {
        items: rows,
      });

      if (res.data.success) {
        toast.success("Feedback categories added successfully");
        navigate("/master/feedback-categorylist");
      }
    } catch (error) {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      
      {/* HEADER CARD - Full Width */}
      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">
        <div className="card-body d-flex align-items-center justify-content-between p-3">
          <div>
            <h4 className="mb-1 fw-bold text-dark">Add Feedback Category</h4>
            <p className="text-muted small mb-0">Create new main categories for feedback</p>
          </div>
          
          <Link
            to="/master/feedback-categorylist"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} /> Back to List
          </Link>
        </div>
      </div>

      {/* FORM CARD - Full Width */}
      <div className="card shadow-sm border-0 rounded-3">
        <div className="card-body p-4">
          
          <div className="d-flex align-items-center justify-content-between mb-3">
            <label className="form-label fw-bold text-secondary small text-uppercase mb-0">
              Categories List
            </label>
            <span className="badge bg-light text-secondary border">
              {rows.length} Items
            </span>
          </div>

          <div className="table-responsive mb-3 border rounded-3">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="text-center py-3 text-secondary small fw-bold" style={{ width: "5%" }}>#</th>
                  <th className="py-3 text-secondary small fw-bold" style={{ width: "85%" }}>Category Name</th>
                  <th className="py-3 text-center text-secondary small fw-bold" style={{ width: "10%" }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td className="text-center text-muted fw-bold">{index + 1}</td>
                    <td>
                      <input
                        type="text"
                        className="form-control border-0 bg-transparent p-0"
                        style={{ boxShadow: 'none' }}
                        placeholder="e.g. Vehicle Condition, Driver Behavior..."
                        value={row.item_name}
                        onChange={(e) => handleChange(index, e.target.value)}
                        autoFocus={index === rows.length - 1 && index > 0}
                      />
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-light text-danger btn-sm border-0 hover-shadow"
                        onClick={() => removeRow(index)}
                        disabled={rows.length === 1}
                        title="Remove"
                        style={{ width: "32px", height: "32px", borderRadius: "50%" }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ACTION BUTTONS */}
          <div className="d-flex justify-content-between align-items-center mt-4">
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 border-dashed px-3 py-2"
              style={{ borderStyle: "dashed", borderWidth: "2px" }}
              onClick={addMore}
            >
              <Plus size={16} /> Add Another Row
            </button>

            <button
              className="btn btn-success d-flex align-items-center gap-2 px-4 py-2 fw-medium"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <div className="spinner-border spinner-border-sm text-white" role="status"></div>
              ) : (
                <CheckCircle size={18} />
              )}
              Save Categories
            </button>
          </div>

        </div>
      </div>

    </div>
  );
};

export default FeedbackCategoryAddPage;